import { useState, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { resolveImageUrl } from '@/utils/imageUrl';
import { toast } from 'sonner';
import {
  X, Loader2, Image as ImageIcon, Send, Hash, Sparkles,
  ShieldCheck, Newspaper, Trophy, Lightbulb, Calendar, Handshake,
} from 'lucide-react';

/**
 * AgentComposeModal — inline post composer for verified MARA agents.
 * Posts to /api/posts (same backend as students).
 *
 * Categories follow the "Post to Flow" taxonomy: Free Consultation
 * (hero, teal-filled), Visa Update, Visa Insight, Success Story, Tip, Event.
 * Category is REQUIRED — Post button stays disabled until one is picked.
 * Placeholder swaps per category (matches pattern in CreatePostPage.jsx).
 */
const AGENT_CATEGORIES = [
  { id: 'free_consultation', label: 'Consultation Offer', icon: Handshake,   hero: true,  accent: 'bg-teal-600 text-white border-teal-600 shadow-md shadow-teal-200', heroIdle: 'bg-teal-50 text-teal-700 border-teal-300', placeholder: "Describe your consultation offer. Include your normal fee, the offer price or discount, what you cover, duration, and how students can book." },
  { id: 'immigration_news',  label: 'Visa & Migration News', icon: Newspaper,   accent: 'border-violet-300 text-violet-700 bg-violet-50', placeholder: "Share your expert insight on a visa topic or recent rule change. Be specific and practical for students." },
  { id: 'success_story',     label: 'Success Story',     icon: Trophy,      accent: 'border-emerald-300 text-emerald-700 bg-emerald-50', placeholder: "Share a client success story. What was the outcome, how long did it take, and what made the difference?" },
  { id: 'tip',               label: 'Tip',               icon: Lightbulb,   accent: 'border-amber-300 text-amber-700 bg-amber-50',   placeholder: "Share a practical tip for international students. Be specific and actionable." },
  { id: 'event',             label: 'Event',             icon: Calendar,    accent: 'border-rose-300 text-rose-700 bg-rose-50',      placeholder: "Describe your event. Include date, location, what students will learn and how to register." },
];

const DEFAULT_PLACEHOLDER = "Select a category above to start posting to Flow.";

export default function AgentComposeModal({ agent, onClose, onPosted }) {
  const [content, setContent] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [category, setCategory] = useState('');
  const [originalFee, setOriginalFee] = useState('');
  const [offerPrice, setOfferPrice] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [uploadedUrl, setUploadedUrl] = useState(null);
  const [posting, setPosting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const initials = (agent.name || 'A').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const activeCat = category ? AGENT_CATEGORIES.find(c => c.id === category) : null;
  const placeholder = activeCat?.placeholder || DEFAULT_PLACEHOLDER;
  const canPost = !!category && (content.trim() || uploadedUrl) && !posting && !uploading;

  const pickImage = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error('Image must be < 8MB'); return; }
    setImageFile(file);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/posts/upload-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadedUrl(res.data?.url || res.data?.image_url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
      setImageFile(null);
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    const text = content.trim();
    if (!category) { toast.error('Pick a category first'); return; }
    if (!text && !uploadedUrl) { toast.error('Add some text or a photo'); return; }
    setPosting(true);
    try {
      const tags = hashtags.split(/[\s,]+/).map(t => t.replace(/^#/, '').trim()).filter(Boolean);
      const mediaArr = uploadedUrl ? [{ url: uploadedUrl, type: 'image' }] : [];
      const payload = {
        content: text,
        category,
        hashtags: tags,
        images: uploadedUrl ? [uploadedUrl] : [],
        media: mediaArr,
        visibility: 'public',
      };
      // Consultation Offer extras → goes into post_metadata (existing
      // post schema supports arbitrary fields, picked up by the card)
      if (category === 'free_consultation' && (originalFee.trim() || offerPrice.trim())) {
        payload.post_metadata = {
          original_fee: originalFee.trim(),
          offer_price: offerPrice.trim(),
        };
      }
      const res = await axios.post(`${API}/posts`, payload);
      // Prefer the canonical saved post from the backend; fall back to optimistic build
      const saved = res.data?.post;
      const fullPost = saved ? {
        ...saved,
        // Normalize for the AgentPublicProfile post card which expects these fields
        author_id: saved.user_id,
        author_name: saved.user_name || agent.name,
        author_role: saved.user_role || 'agent',
        likes_count: saved.like_count || 0,
        comments_count: saved.comment_count || 0,
        bookmarks_count: saved.bookmark_count || 0,
        views_count: 0,
      } : {
        post_id: res.data?.post_id,
        author_id: agent.user_id,
        author_name: agent.name,
        author_role: 'agent',
        content: text,
        category: category || 'general',
        hashtags: tags,
        images: payload.images,
        media: mediaArr,
        likes_count: 0,
        comments_count: 0,
        bookmarks_count: 0,
        views_count: 0,
        created_at: new Date().toISOString(),
      };
      toast.success('Posted! Visible on your profile and feed.');
      onPosted?.(fullPost);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to post');
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white w-full sm:w-[560px] sm:max-h-[88vh] max-h-[92vh] sm:rounded-3xl rounded-t-3xl flex flex-col shadow-2xl" onClick={e => e.stopPropagation()} data-testid="agent-compose-modal">

        {/* Header */}
        <header className="flex items-center justify-between p-4 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-500 text-white flex items-center justify-center">
              <Sparkles className="h-4 w-4" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-800">Post to Flow</h3>
              <p className="text-[10px] text-slate-400">Visible in Flow and student profiles</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center" data-testid="compose-close">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </header>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* Author + textarea */}
          <div className="flex gap-3">
            {agent.profile_picture ? (
              <img src={resolveImageUrl(agent.profile_picture)} alt="" className="w-10 h-10 rounded-full object-cover shrink-0 border border-slate-200" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 text-white text-xs font-bold flex items-center justify-center shrink-0">{initials}</div>
            )}
            <textarea
              value={content}
              onChange={e => setContent(e.target.value.slice(0, 2500))}
              rows={5}
              placeholder={placeholder}
              className="flex-1 bg-transparent border-none outline-none resize-none text-sm text-slate-700 placeholder:text-slate-300 leading-relaxed min-h-[100px]"
              data-testid="compose-textarea"
              autoFocus
            />
          </div>

          {/* Image preview */}
          {imageFile && (
            <div className="relative rounded-2xl overflow-hidden border border-slate-100" data-testid="compose-image-preview">
              <img src={uploadedUrl ? resolveImageUrl(uploadedUrl) : URL.createObjectURL(imageFile)} alt="" className="w-full max-h-72 object-cover" />
              {uploading && (
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                  <Loader2 className="h-5 w-5 text-white animate-spin" />
                </div>
              )}
              <button onClick={() => { setImageFile(null); setUploadedUrl(null); }} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center" data-testid="compose-remove-image">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}

          {/* Consultation Offer extras — Original Fee + Offer Price.
              Both optional. If both filled, a price badge renders on the card. */}
          {category === 'free_consultation' && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3" data-testid="compose-offer-prices">
              <p className="text-[10px] font-black text-amber-700 uppercase tracking-widest mb-2">
                Offer Details <span className="text-slate-400">(OPTIONAL)</span>
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] font-bold text-slate-500 block mb-1">Original Fee</label>
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 font-bold">$</span>
                    <input
                      value={originalFee}
                      onChange={e => setOriginalFee(e.target.value.replace(/[^\d.]/g, '').slice(0, 8))}
                      placeholder="200"
                      inputMode="decimal"
                      className="w-full pl-5 pr-2 py-1.5 rounded-lg border border-slate-200 bg-white text-xs focus:outline-none focus:border-amber-400"
                      data-testid="compose-original-fee"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-bold text-slate-500 block mb-1">Offer Price</label>
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 font-bold">$</span>
                    <input
                      value={offerPrice}
                      onChange={e => setOfferPrice(e.target.value.replace(/[^\d.]/g, '').slice(0, 8))}
                      placeholder="99"
                      inputMode="decimal"
                      className="w-full pl-5 pr-2 py-1.5 rounded-lg border border-slate-200 bg-white text-xs focus:outline-none focus:border-amber-400"
                      data-testid="compose-offer-price"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Category pills — REQUIRED, single-select. Free Consultation is
              the hero pill (teal filled, larger, first position). */}
          <div>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">
              Category <span className="text-rose-500">(REQUIRED)</span>
            </p>
            <div className="flex gap-1.5 flex-wrap" data-testid="compose-categories">
              {AGENT_CATEGORIES.map(c => {
                const Icon = c.icon;
                const active = category === c.id;
                let pillClass;
                if (c.hero) {
                  pillClass = `px-3.5 py-1.5 text-[12px] ${active ? c.accent : c.heroIdle}`;
                } else {
                  pillClass = `px-2.5 py-1 text-[11px] ${active ? c.accent : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`;
                }
                return (
                  <button
                    key={c.id}
                    onClick={() => setCategory(active ? '' : c.id)}
                    className={`flex items-center gap-1 rounded-full font-bold border transition-all ${pillClass}`}
                    data-testid={`compose-cat-${c.id}`}
                  >
                    <Icon className={c.hero ? 'h-3.5 w-3.5' : 'h-3 w-3'} />
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Hashtags */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-50 border border-slate-100">
            <Hash className="h-3.5 w-3.5 text-slate-400 shrink-0" />
            <input
              value={hashtags}
              onChange={e => setHashtags(e.target.value)}
              placeholder="Add hashtags (e.g. #485visa #pr2026)"
              className="flex-1 bg-transparent text-xs text-slate-600 placeholder:text-slate-300 outline-none"
              data-testid="compose-hashtags"
            />
          </div>
        </div>

        {/* Footer */}
        <footer className="flex items-center justify-between gap-2 p-4 border-t border-slate-100 shrink-0">
          <div className="flex items-center gap-1">
            <button onClick={() => fileRef.current?.click()} className="flex items-center gap-1 px-3 py-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-700 text-xs font-bold transition-colors" data-testid="compose-pick-image">
              <ImageIcon className="h-3.5 w-3.5" /> Photo
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={e => pickImage(e.target.files?.[0])} />
            </button>
            <span className={`text-[10px] ${content.length > 2300 ? 'text-rose-500 font-bold' : 'text-slate-400'}`}>
              {content.length}/2500
            </span>
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-xs font-bold text-slate-700">Cancel</button>
            <button onClick={submit} disabled={!canPost} className="px-4 py-1.5 rounded-lg bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5" data-testid="compose-submit">
              {posting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Send className="h-3.5 w-3.5" /> Post</>}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
